import React, { Children, useMemo, useRef, useState } from 'react';
import { arrowWidth, NavigationArrows } from './navigation-arrows';
import { createStyles } from '@/contexts/theme-context';
import { dotDiameter, NavigationDots } from './navigation-dots';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  StatusBar,
  useWindowDimensions,
  View,
} from 'react-native';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL } from '@/styles';
import { SIZES } from './button';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WelcomeContainerContent } from './welcome-container-content';
import { WelcomeHeader } from './welcome-header';
import { WelcomePageButtonConfig } from './welcome-page-types';

interface Props {
  children: React.ReactNode;
  welcomeButtons: WelcomePageButtonConfig[];
}

export const navigationArrowWidth = arrowWidth + 2 * SCREEN_PADDING_SMALL;

export const EXTRA_SMALL_MAX_WIDTH = 390;

export const useFooterHeight = (buttonCount: number) => {
  const { bottom } = useSafeAreaInsets();
  const buttonSize = SIZES.medium;
  const navigationDotHeight = Math.max(bottom, SCREEN_PADDING) + dotDiameter;
  return {
    articleFooterHeight: navigationDotHeight + buttonSize + 2 * SCREEN_PADDING,
    welcomeFooterHeight:
      navigationDotHeight +
      buttonCount * buttonSize +
      buttonCount * SCREEN_PADDING,
  };
};

export const WelcomePageContainer: React.FC<Props> = ({
  children,
  welcomeButtons,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const [styles, theme] = useStyles();
  const [activeIndex, setActiveIndex] = useState(0);
  const [welcomeScreenActive, setWelcomeScreenActive] = useState(true);
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollViewCount = useMemo(() => {
    return Children.toArray(children).filter(Boolean).length;
  }, [children]);
  const { isLargeLayout } = useLayoutContext();

  const scrollToIndex = (index: number) => {
    scrollViewRef.current?.scrollTo({
      x: index * width,
    });
    setActiveIndex(index);
    setTimeout(() => {
      setWelcomeScreenActive(index === 0);
    }, 250);
  };

  return (
    <>
      <StatusBar backgroundColor={theme.colors.transparent} />
      <View style={styles.contentContainer}>
        <ScrollView
          bounces={false}
          horizontal
          nestedScrollEnabled
          onMomentumScrollEnd={(e: NativeSyntheticEvent<NativeScrollEvent>) => {
            const index = Math.round(e.nativeEvent.contentOffset.x / width);
            setWelcomeScreenActive(index === 0);
            setActiveIndex(index);
          }}
          pagingEnabled
          ref={scrollViewRef}
          // onMomentumScrollEnd doesn't work on web
          // https://github.com/necolas/react-native-web/issues/1021
          scrollEnabled={Platform.OS !== 'web'}
        >
          {children}
        </ScrollView>
        <View
          pointerEvents="box-none"
          style={[
            styles.welcomeOverlayContainer,
            { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
          ]}
        >
          <WelcomeHeader welcomeScreenActive={welcomeScreenActive} />
          {scrollViewCount > 1 ? (
            <View
              style={isLargeLayout ? styles.navigationDotsContainerLarge : null}
            >
              <NavigationDots
                activeIndex={activeIndex}
                activeStyle={styles.activeDots}
                count={scrollViewCount}
                onChange={scrollToIndex}
              />
            </View>
          ) : null}
        </View>
        <WelcomeContainerContent
          welcomeButtons={welcomeButtons}
          welcomeScreenActive={welcomeScreenActive}
        />
        {Platform.OS === 'web' && scrollViewCount > 1 ? (
          <NavigationArrows
            activeIndex={activeIndex}
            backgroundColor="rgba(255,255,255,0.5)"
            buttonContainerStyle={styles.navigationArrowsContainer}
            count={scrollViewCount}
            onChange={scrollToIndex}
          />
        ) : null}
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  activeDots: {
    backgroundColor: theme.colors.accent,
  },
  contentContainer: {
    flex: 1,
  },
  navigationArrowsContainer: {
    borderRadius: 4,
    height: 40,
    marginHorizontal: SCREEN_PADDING_SMALL,
    marginVertical: 'auto',
  },
  navigationDotsContainerLarge: {
    alignSelf: 'flex-start',
  },
  welcomeOverlayContainer: {
    flex: 1,
    height: '100%',
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: SCREEN_PADDING,
    position: 'absolute',
    width: '100%',
  },
}));
